import React from 'react';
import type { EntityType, Entity, QueryFilter } from '../types';
import type { ReferencedEntity, RelatedEntities } from './entityDetailTypes';
import type { ResolvedEntityMap } from '../utils/resolveColumns';
import { ENTITY_COLUMNS } from '../config/viewsConfig';
import type { ColumnDef } from '../config/viewsConfig';
import { renderCellValue } from '../utils/renderCellValue';
import { pluralizeEntityType } from '../utils/formatValue';
import './EntityDetail.css';

interface RelationshipsTabProps {
  entityType: EntityType;
  entityId: number;
  referencedEntities: ReferencedEntity[];
  relatedEntities: RelatedEntities;
  referencingFullEntities: Record<string, Entity[]>;
  referencingResolvedEntities: Record<string, ResolvedEntityMap>;
  onEntityClick: (entityType: EntityType, entityId: number) => void;
  onNavigate: (path: string) => void;
}

function getColumnsForType(type: EntityType, excludeBackRef?: EntityType): ColumnDef[] {
  const columns = ENTITY_COLUMNS[type] || [];
  if (!excludeBackRef) return columns;
  const backRefKey = `${excludeBackRef}_id`;
  return columns.filter(col => col.key !== backRefKey && !col.key.startsWith(backRefKey + '.'));
}

export const EntityDetailRelationshipsTab: React.FC<RelationshipsTabProps> = ({
  entityType,
  entityId,
  referencedEntities,
  relatedEntities,
  referencingFullEntities,
  referencingResolvedEntities,
  onEntityClick,
  onNavigate,
}) => {
  const renderEntityTable = (entities: Entity[], type: EntityType, resolvedEntities?: ResolvedEntityMap, excludeBackRef?: EntityType) => {
    const columns = getColumnsForType(type, excludeBackRef);
    const cellOptions = {
      onEntityClick: (id: number) => onEntityClick(type, id),
      onNavigate,
      resolvedEntities,
    };

    return (
      <table className="relationship-table">
        <thead>
          <tr>
            {columns.map(col => <th key={col.key}>{col.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {entities.map(e => (
            <tr key={e.id}>
              {columns.map(col => (
                <td key={col.key}>{renderCellValue(e, col, cellOptions)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const hasOutgoing = referencedEntities.length > 0;
  const hasIncoming = Object.keys(relatedEntities).length > 0;

  if (!hasOutgoing && !hasIncoming) {
    return <p className="no-relationships">No relationships found for this {entityType}.</p>;
  }

  const referencedByType: Record<string, Entity[]> = {};
  for (const ref of referencedEntities) {
    if (ref.entity) {
      if (!referencedByType[ref.entityType]) referencedByType[ref.entityType] = [];
      referencedByType[ref.entityType].push(ref.entity);
    }
  }

  return (
    <>
      {hasOutgoing && (
        <div className="entity-detail-section">
          <h3>Referenced ({referencedEntities.length})</h3>
          <p className="section-description">Entities that this {entityType} references</p>
          {Object.entries(referencedByType).map(([refType, entities]) => (
            <div key={refType} className="relationship-group">
              <h4 className="relationship-group-heading">
                {pluralizeEntityType(refType as EntityType)} ({entities.length})
              </h4>
              {renderEntityTable(entities, refType as EntityType)}
            </div>
          ))}
        </div>
      )}

      {hasIncoming && (
        <div className="entity-detail-section">
          <h3>Referencing</h3>
          <p className="section-description">Entities that reference this {entityType}</p>
          {Object.entries(relatedEntities).map(([relatedType, relatedData]) => {
            if (!relatedData) return null;
            const hasMore = relatedData.totalCount > relatedData.ids.length;
            const handleSeeAll = () => {
              const filter: QueryFilter = {
                attribute_name: relatedData.attributeName,
                operator: '=',
                value: entityId,
              };
              const filtersParam = encodeURIComponent(JSON.stringify([filter]));
              onNavigate(`/${relatedType}?filters=${filtersParam}`);
            };

            const fullEntities = referencingFullEntities[relatedType] || [];
            const resolved = referencingResolvedEntities[relatedType];

            return (
              <div key={relatedType} className="relationship-group">
                <h4 className="relationship-group-heading">
                  {pluralizeEntityType(relatedType as EntityType)} ({relatedData.totalCount})
                </h4>
                {fullEntities.length > 0
                  ? renderEntityTable(fullEntities, relatedType as EntityType, resolved, entityType)
                  : (
                    <table className="relationship-table">
                      <thead>
                        <tr><th>ID</th></tr>
                      </thead>
                      <tbody>
                        {relatedData.ids.map((id) => (
                          <tr key={id}>
                            <td>
                              <button
                                className="entity-link"
                                onClick={() => onEntityClick(relatedType as EntityType, id)}
                              >
                                {id}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                }
                {hasMore && (
                  <div className="see-all-link">
                    <button onClick={handleSeeAll} className="see-all-button">
                      See all {relatedData.totalCount}{' '}
                      {pluralizeEntityType(relatedType as EntityType).toLowerCase()}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};
